import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { JsonEditor } from '../../_shared/components/JsonEditor';
import { routeFormSchema } from '../schema/routeSchema';
import { useCreateRoute, useReplaceRoute, useServiceOptions } from '../hooks/useRoutes';
import type { RouteFormValues } from '../schema/routeSchema';
import type { RouteRow } from '../types/route';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'] as const;

type ToastKind = 'success' | 'error';

interface RouteFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: RouteRow | null;
  onClose: () => void;
  onSaved?: () => void;
}

export function RouteFormDialog({ open, mode, initial, onClose, onSaved }: RouteFormDialogProps) {
  const { t } = useTranslation();
  const [toast, setToast] = useState<{ message: string; kind: ToastKind } | null>(null);

  const form = useForm<RouteFormValues>({
    resolver: zodResolver(routeFormSchema),
    defaultValues: initial
      ? {
          name: initial.name,
          serviceId: initial.serviceId,
          paths: initial.paths,
          methods: initial.methods as RouteFormValues['methods'],
          stripPath: initial.stripPath,
          preserveHost: initial.preserveHost,
          priority: initial.priority,
          enabled: initial.enabled,
        }
      : {
          name: '',
          serviceId: '',
          paths: [''],
          methods: ['GET'],
          stripPath: true,
          preserveHost: false,
          priority: 0,
          enabled: true,
        },
  });

  const { data: services = [] } = useServiceOptions();
  const createRoute = useCreateRoute(
    (name) => {
      // #441 (UAT 2026-05-13): 등록 성공 시 확인 방법 안내 메시지로 갱신 (어디서 어떻게 확인 가능한지).
      // 길이가 길어지므로 표시 시간 약간 늘림(2500ms).
      setToast({ message: t('gateway.routes.toast.createdVerify', { name }), kind: 'success' });
      setTimeout(() => { setToast(null); onSaved?.(); onClose(); }, 2500);
    },
    (err) => {
      const msg = (err as { message?: string })?.message ?? t('gateway.shared.error.message');
      setToast({ message: msg, kind: 'error' });
    },
  );
  const replaceRoute = useReplaceRoute(
    initial?.id ?? '',
    () => {
      setToast({ message: t('gateway.routes.toast.updated'), kind: 'success' });
      setTimeout(() => { setToast(null); onSaved?.(); onClose(); }, 1500);
    },
    (err) => {
      const msg = (err as { message?: string })?.message ?? t('gateway.shared.error.message');
      setToast({ message: msg, kind: 'error' });
    },
  );

  // Reset form when initial changes
  useEffect(() => {
    if (open && initial) {
      form.reset({
        name: initial.name,
        serviceId: initial.serviceId,
        paths: initial.paths,
        methods: initial.methods as RouteFormValues['methods'],
        stripPath: initial.stripPath,
        preserveHost: initial.preserveHost,
        priority: initial.priority,
        enabled: initial.enabled,
      });
    } else if (open && !initial) {
      form.reset({
        name: '',
        serviceId: '',
        paths: [''],
        methods: ['GET'],
        stripPath: true,
        preserveHost: false,
        priority: 0,
        enabled: true,
      });
    }
  }, [open, initial, form]);

  const watchedValues = form.watch();
  const isPending = createRoute.isPending || replaceRoute.isPending;

  const onSubmit = form.handleSubmit((values) => {
    // #414: serviceId 로 serviceName 조회해 함께 전송
    const selectedService = services.find((s) => s.id === values.serviceId);
    const enriched = {
      ...values,
      serviceName: selectedService?.name ?? values.serviceId,
    };
    if (mode === 'create') {
      createRoute.mutate(enriched);
    } else if (initial) {
      replaceRoute.mutate(enriched);
    }
  });

  const handleAddPath = () => {
    const current = form.getValues('paths');
    form.setValue('paths', [...current, '']);
  };

  const handleRemovePath = (index: number) => {
    const current = form.getValues('paths');
    form.setValue('paths', current.filter((_, i) => i !== index));
  };

  return (
    <FormDialog
      open={open}
      title={mode === 'create' ? t('gateway.routes.form.create') : t('gateway.routes.form.edit')}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={isPending ? t('gateway.shared.actions.saving') : t('gateway.shared.actions.save')}
      cancelLabel={t('gateway.shared.actions.cancel')}
    >
      {toast && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            marginBottom: 'var(--spacing-3)',
            padding: 'var(--spacing-2) var(--spacing-3)',
            background: toast.kind === 'error' ? 'var(--color-danger)' : 'var(--color-success)',
            color: 'var(--color-text-inverse)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {toast.message}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--spacing-6)',
        }}
      >
        {/* Left: Form Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          {/* Name */}
          <div>
            <label
              htmlFor="route-name"
              style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--spacing-1)' }}
            >
              {t('gateway.routes.form.name.label')}
            </label>
            <input
              id="route-name"
              {...form.register('name')}
              placeholder={t('gateway.routes.form.name.placeholder')}
              aria-invalid={!!form.formState.errors.name}
              aria-describedby={form.formState.errors.name ? 'route-name-error' : undefined}
              style={{
                width: '100%',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: `1px solid ${form.formState.errors.name ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                boxSizing: 'border-box',
              }}
            />
            {form.formState.errors.name && (
              <span id="route-name-error" role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
                {form.formState.errors.name.message}
              </span>
            )}
          </div>

          {/* Service */}
          <div>
            <label
              htmlFor="route-service"
              style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--spacing-1)' }}
            >
              {t('gateway.routes.form.service.label')}
            </label>
            <select
              id="route-service"
              {...form.register('serviceId')}
              aria-invalid={!!form.formState.errors.serviceId}
              style={{
                width: '100%',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: `1px solid ${form.formState.errors.serviceId ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                boxSizing: 'border-box',
              }}
            >
              <option value="">{t('gateway.routes.form.service.placeholder')}</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {form.formState.errors.serviceId && (
              <span role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
                {form.formState.errors.serviceId.message}
              </span>
            )}
          </div>

          {/* Paths */}
          <div>
            <label
              style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--spacing-1)' }}
            >
              {t('gateway.routes.form.paths.label')}
            </label>
            {form.watch('paths').map((_, index) => (
              <div key={index} style={{ display: 'flex', gap: 'var(--spacing-1)', marginBottom: 'var(--spacing-1)' }}>
                <input
                  {...form.register(`paths.${index}`)}
                  placeholder={t('gateway.routes.form.paths.placeholder')}
                  aria-label={`${t('gateway.routes.form.paths.label')} ${index + 1}`}
                  style={{
                    flex: 1,
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                />
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => handleRemovePath(index)}
                    aria-label={`${t('gateway.routes.form.paths.label')} ${index + 1} ${t('gateway.shared.actions.delete')}`}
                    style={{
                      padding: 'var(--spacing-2)',
                      border: '1px solid var(--color-danger)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--color-danger)',
                      background: 'var(--color-surface-background)',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    -
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddPath}
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-brand-primary)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              {t('gateway.routes.form.paths.add')}
            </button>
            {form.formState.errors.paths && (
              <span role="alert" style={{ display: 'block', color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
                {form.formState.errors.paths.message}
              </span>
            )}
          </div>

          {/* Methods */}
          <div>
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--spacing-1)' }}>
                {t('gateway.routes.form.methods.label')}
              </legend>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
                {HTTP_METHODS.map((method) => {
                  const methods = form.watch('methods') ?? [];
                  const checked = methods.includes(method);
                  return (
                    <label
                      key={method}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-1)',
                        fontSize: 'var(--font-size-sm)',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const current = form.getValues('methods') ?? [];
                          if (e.target.checked) {
                            form.setValue('methods', [...current, method] as RouteFormValues['methods']);
                          } else {
                            form.setValue('methods', current.filter((m) => m !== method) as RouteFormValues['methods']);
                          }
                        }}
                        aria-label={`${method}`}
                      />
                      {method}
                    </label>
                  );
                })}
              </div>
            </fieldset>
          </div>

          {/* Flags */}
          <div style={{ display: 'flex', gap: 'var(--spacing-4)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)' }}>
              <input type="checkbox" {...form.register('stripPath')} />
              {t('gateway.routes.form.stripPath.label')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)' }}>
              <input type="checkbox" {...form.register('preserveHost')} />
              {t('gateway.routes.form.preserveHost.label')}
            </label>
          </div>

          {/* Priority */}
          <div>
            <label
              htmlFor="route-priority"
              style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--spacing-1)' }}
            >
              {t('gateway.routes.form.priority.label')}
            </label>
            <input
              id="route-priority"
              type="number"
              min={0}
              max={100}
              {...form.register('priority', { valueAsNumber: true })}
              style={{
                width: '100px',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
              }}
            />
          </div>

          {/* Enabled */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)' }}>
            <input type="checkbox" {...form.register('enabled')} />
            {t('gateway.routes.form.enabled.label')}
          </label>
        </div>

        {/* Right: JSON Preview */}
        <div>
          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--spacing-2)' }}>
            {t('gateway.routes.form.jsonPreview')}
          </div>
          <JsonEditor
            value={watchedValues}
            readOnly
            height={360}
            ariaLabel={t('gateway.routes.form.jsonPreview')}
          />
        </div>
      </div>
    </FormDialog>
  );
}
